import * as React from "react";
import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";
import { useReveal } from "@/hooks/useReveal";

/**
 * Shared presentation primitives for the Phase 2 homepage.
 *
 * These wrap the Phase 1 motion + token system rather than replacing it:
 * `useReveal` drives the scroll reveal, and every colour comes from a
 * semantic token defined in src/styles.css.
 */

/** Section shell — enforces the eyebrow → heading → intro → body hierarchy. */
export function Section({
  id,
  tone = "base",
  className,
  children,
  labelledBy,
}: {
  id?: string;
  /** `base` = page background, `raised` = subtle surface band. */
  tone?: "base" | "raised";
  className?: string;
  children: ReactNode;
  labelledBy?: string;
}) {
  return (
    <section
      id={id}
      aria-labelledby={labelledBy}
      className={`relative ${
        tone === "raised"
          ? "border-y border-border bg-[color-mix(in_oklab,var(--surface)_55%,transparent)]"
          : ""
      } ${className ?? ""}`}
    >
      {children}
    </section>
  );
}

export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <p className="inline-flex items-center gap-2 text-[0.7rem] font-medium uppercase tracking-[0.22em] text-primary">
      <span aria-hidden className="h-1 w-1 rounded-full bg-primary" />
      {children}
    </p>
  );
}

/** Consistent section header block. */
export function SectionHeader({
  id,
  eyebrow,
  title,
  intro,
  align = "left",
  className,
}: {
  id?: string;
  eyebrow: string;
  title: ReactNode;
  intro?: ReactNode;
  align?: "left" | "center";
  className?: string;
}) {
  return (
    <div
      className={`max-w-3xl ${align === "center" ? "mx-auto text-center" : ""} ${
        className ?? ""
      }`}
    >
      <Eyebrow>{eyebrow}</Eyebrow>
      <h2
        id={id}
        className="mt-5 text-balance font-display text-3xl leading-[1.06] sm:text-4xl lg:text-5xl"
      >
        {title}
      </h2>
      {intro ? (
        <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
          {intro}
        </p>
      ) : null}
    </div>
  );
}

/** Reveal wrapper built on the Phase 1 `.reveal` CSS (reduced-motion safe). */
export function Rise({
  delay = 0,
  as = "div",
  className,
  children,
}: {
  delay?: 0 | 1 | 2 | 3 | 4;
  as?: "div" | "li" | "article" | "figure";
  className?: string;
  children: ReactNode;
}) {
  const { ref, className: revealClass } = useReveal<HTMLDivElement>();
  const Tag = as as "div";
  return (
    <Tag
      ref={ref}
      className={`${revealClass}${delay ? ` reveal-delay-${delay}` : ""} ${
        className ?? ""
      }`}
    >
      {children}
    </Tag>
  );
}

const CONTACT_SEARCH = { location: "", service: "" } as const;

/** Primary conversion action — one phrase, one destination, sitewide. */
export function PrimaryCta({
  className,
  label = "Book a Strategy Call",
}: {
  className?: string;
  label?: string;
}) {
  return (
    <Link
      to="/contact/"
      search={CONTACT_SEARCH}
      className={`group inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-transform duration-300 hover:-translate-y-0.5 ${
        className ?? ""
      }`}
    >
      {label}
      <span
        aria-hidden
        className="transition-transform duration-300 group-hover:translate-x-1"
      >
        →
      </span>
    </Link>
  );
}

/** Secondary conversion action. */
export function SecondaryCta({
  className,
  label = "View Case Studies",
}: {
  className?: string;
  label?: string;
}) {
  return (
    <Link
      to="/case-studies/"
      className={`group inline-flex items-center gap-2 rounded-full border border-border-strong bg-background/40 px-6 py-3 text-sm font-medium text-foreground transition-colors duration-300 hover:border-primary ${
        className ?? ""
      }`}
    >
      {label}
      <span
        aria-hidden
        className="transition-transform duration-300 group-hover:translate-x-1"
      >
        →
      </span>
    </Link>
  );
}

/**
 * Count-up for verified metrics only. Animates once on entry, respects
 * reduced motion, and never changes layout width (the final string is
 * reserved via a hidden sizing span).
 */
export function CountUp({
  value,
  prefix = "",
  suffix = "",
  decimals = 0,
  durationMs = 900,
}: {
  value: number;
  prefix?: string;
  suffix?: string;
  decimals?: number;
  durationMs?: number;
}) {
  const finalText = `${prefix}${value.toFixed(decimals)}${suffix}`;
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const [display, setDisplay] = React.useState(finalText);

  React.useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduced || typeof IntersectionObserver === "undefined") return;

    let raf = 0;
    let start = 0;
    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const step = (ts: number) => {
          if (!start) start = ts;
          const p = Math.min((ts - start) / durationMs, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setDisplay(`${prefix}${(value * eased).toFixed(decimals)}${suffix}`);
          if (p < 1) raf = requestAnimationFrame(step);
        };
        setDisplay(`${prefix}${(0).toFixed(decimals)}${suffix}`);
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, prefix, suffix, decimals, durationMs]);

  return (
    <span ref={ref} className="tabular-nums">
      <span aria-hidden>{display}</span>
      <span className="sr-only">{finalText}</span>
    </span>
  );
}
